import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Event, Lead, PayloadUser } from '@/types'

interface OptimisticHistoryState {
  // Map of leadId -> optimistic events
  optimisticEvents: Record<number, Event[]>
}

const initialState: OptimisticHistoryState = {
  optimisticEvents: {}
}

const optimisticHistorySlice = createSlice({
  name: 'optimisticHistory',
  initialState,
  reducers: {
    addOptimisticEmail(state, action: PayloadAction<{
      leadId: number
      tempId?: number
      email: {
        to: string
        from: string
        subject: string
        textAsHtml: string
        userId: number
        username: string
        userImg?: string
        planned?: boolean
        happening?: Date
      }
    }>) {
      const { leadId, email } = action.payload
      const now = new Date().toISOString()

      // Use provided tempId or generate one
      const tempId = action.payload.tempId ?? -Date.now()

      const optimisticEvent: Event = {
        id: tempId,
        event_type: 'MAIL',
        happening: email.happening || new Date(),
        attributes: {
          type: 'SEND_INFO', // Default event type for sent emails
          happening: email.happening?.toISOString() || now,
          note: '',
          subject: email.subject,
          from: email.from,
          to: email.to,
          direction: 'OUTBOUND',
          plan: email.planned || false,
          textAsHtml: email.textAsHtml,
          text: email.textAsHtml.replace(/<[^>]*>/g, ''), // Strip HTML
          status: email.planned ? 'planned' : 'sent',
          createdAt: now,
          updatedAt: now,
          recording: undefined,
          lead: { id: leadId } as unknown as Lead,
          user: {
            data: {
              id: email.userId,
              attributes: {
                username: email.username,
                img: email.userImg
              }
            } as unknown as PayloadUser
          }
        }
      }

      if (!state.optimisticEvents[leadId]) {
        state.optimisticEvents[leadId] = []
      }
      state.optimisticEvents[leadId].unshift(optimisticEvent)
    },

    addOptimisticNote(state, action: PayloadAction<{
      leadId: number
      tempId?: number
      note: {
        content: string
        userId: number
        username: string
        userImg?: string
      }
    }>) {
      const { leadId, note } = action.payload
      const now = new Date().toISOString()
      const tempId = action.payload.tempId ?? -Date.now()

      const optimisticEvent: Event = {
        id: tempId,
        event_type: 'NOTE',
        happening: new Date(),
        attributes: {
          type: 'NOTE',
          happening: now,
          note: note.content,
          subject: '',
          from: '',
          to: '',
          direction: '',
          plan: false,
          textAsHtml: '',
          text: '',
          status: '',
          createdAt: now,
          updatedAt: now,
          recording: undefined,
          lead: { id: leadId } as unknown as Lead,
          user: {
            data: {
              id: note.userId,
              attributes: {
                username: note.username,
                img: note.userImg
              }
            } as unknown as PayloadUser
          }
        }
      }

      if (!state.optimisticEvents[leadId]) {
        state.optimisticEvents[leadId] = []
      }
      state.optimisticEvents[leadId].unshift(optimisticEvent)
    },

    clearOptimisticEvents(state, action: PayloadAction<number>) {
      const leadId = action.payload
      delete state.optimisticEvents[leadId]
    },

    // Update optimistic email with real ID from API response
    updateOptimisticEmailId(state, action: PayloadAction<{
      leadId: number
      tempId: number
      realId: number
    }>) {
      const { leadId, tempId, realId } = action.payload
      const events = state.optimisticEvents[leadId]
      if (events) {
        const event = events.find(e => e.id === tempId)
        if (event) {
          event.id = realId
        }
      }
    },

    // Remove optimistic events that now exist in real data
    reconcileOptimisticEvents(state, action: PayloadAction<{
      leadId: number
      realEventIds: number[]
    }>) {
      const { leadId } = action.payload
      // After real data loads, clear optimistic events
      // The real data will contain the actual events
      delete state.optimisticEvents[leadId]
    },

  }
})

export const {
  addOptimisticEmail,
  addOptimisticNote,
  updateOptimisticEmailId,
  clearOptimisticEvents,
  reconcileOptimisticEvents
} = optimisticHistorySlice.actions

export default optimisticHistorySlice.reducer
